import {
    FocusEventHandler,
    forwardRef,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useRef,
    createContext,
    RefObject,
} from 'react';
import { castRef, DistributiveOmit, genericMemo, useToggle } from '@direct-frontend/stdlib';
import { DOMProps } from '@react-types/shared';
import { Loggable, useLogger } from '@direct-frontend/modules-logger';
import { maxLengthCut } from '@direct-frontend/stdlib';

import { cn } from '../../../lib/css/className/className';
import { FilterItem, FilterItemProps } from '../FilterItem';
import { FilterChip } from '../FilterChip';
import { TestProps } from '../../../internal/types/testTypes';
import { Select, SingleSelectProps } from '../../Select';
import { getSelectTriggerContent } from '../../Select/utils';
import { TriggerProps } from '../../Select/Trigger/types';
import { FilterChipContext } from '../FilterChipContext/FilterChipContext';
import { TextAreaProps, TextInput, TextInputProps } from '../../TextInput';
import { IconPlusSkinny } from '../../../icons';
import { MaskConfig, MaskedTextInput } from '../../MaskedTextInput';
import { useFilterChipAddonSizes } from '../BaseFilterChip';

import './FilterExpression.css';

const cls = cn('FilterExpression');

export interface FilterExpressionProps
    extends Pick<FilterItemProps, 'size' | 'color' | 'onRemove' | 'isDisabled'>,
        TestProps,
        Loggable,
        DOMProps {
    /**
     * Название фильтра
     */
    title: string;
    /** Слот слева от названия фильтра */
    addonLeft?: ReactNode;
    /**
     * Внутренности FilterExpression. Должны состоять из FilterExpressionSelect и FilterExpressionInput
     * @expectedNode `FilterExpressionSelect` | `FilterExpressionInput` | `FilterExpressionAdd`
     */
    children?: ReactNode;
    /**
     * Класс для корневого элемента
     */
    className?: string;
    /**
     * Ошибка, отображается как иконка с тултипом – описанием ошибки
     */
    error?: ReactNode;
}

/**
 * Контекст для передачи рефа FilterExpression вниз к дочерним компонентам
 */
const FilterExpressionRefContext = createContext<RefObject<HTMLDivElement> | null>(null);

/**
 * Компонент составного фильтра-выражения, в котором можно последовательно выбрать:
 * - Дополнительный критерий, например, цель. Опциональный
 * - Оператор, например, <=, >=, =, "содержит" или "не содержит"
 * - Ввести значение, например, число, дату или строку
 *
 * Компонент собирается из составных частей для гибкости – FilterExpressionSelect и FilterExpressionInput.
 * Тажке для типового случая его можно использовать с хуками useFilterExpressionSelect и useFilterExpressionInput.
 * Пример использования можно посмотреть в сторибуке или readme
 */
export const FilterExpression = genericMemo(function FilterExpression(props: FilterExpressionProps) {
    const {
        color,
        size,
        className,
        'data-testid': dataTestId,
        title,
        addonLeft,
        error,
        children,
        logNode,
        ...restProps
    } = props;

    const { LogProvider } = useLogger('FilterExpression', logNode);
    const containerRef = useRef<HTMLDivElement>(null);

    return (
        <LogProvider>
            <FilterExpressionRefContext.Provider value={containerRef}>
                <FilterItem
                    ref={containerRef}
                    color={color}
                    size={size}
                    className={cls({}, [className])}
                    data-testid={dataTestId}
                    keyChip={
                        <FilterChip isDisabled={restProps.isDisabled} addonLeft={addonLeft} error={error}>
                            {title}
                        </FilterChip>
                    }
                    {...restProps}
                >
                    {children}
                </FilterItem>
            </FilterExpressionRefContext.Provider>
        </LogProvider>
    );
});

export type FilterExpressionSelectProps<
    ValueType extends string,
    SectionType extends string = ValueType,
> = DistributiveOmit<SingleSelectProps<ValueType, SectionType>, 'size' | 'color' | 'renderTrigger'>;

/**
 * Подкомпонент FilterExpression, используется для создания чипса-селекта.
 * Используется для выбора дополнительного критерия и оператора фильтра
 */
export const FilterExpressionSelect = genericMemo(function FilterExpressionSelect<
    ValueType extends string,
    SectionType extends string = ValueType,
>(props: FilterExpressionSelectProps<ValueType, SectionType>) {
    const { className, 'data-testid': dataTestId } = props;
    const { color, size } = useContext(FilterChipContext);

    const getRenderTrigger = useCallback(
        function <ValueType extends string>() {
            return forwardRef<HTMLElement, TriggerProps<ValueType>>(function RenderTrigger(triggerProps, forwardedRef) {
                const { value, placeholder } = triggerProps;

                return (
                    <FilterChip {...triggerProps} ref={castRef(forwardedRef, 'div')} data-testid={dataTestId}>
                        {getSelectTriggerContent(value) ?? placeholder}
                    </FilterChip>
                );
            });
        },
        [dataTestId],
    );

    const renderTrigger = useMemo(() => getRenderTrigger<ValueType>(), [getRenderTrigger]);
    const renderDataProviderTrigger = useMemo(() => getRenderTrigger<string>(), [getRenderTrigger]);

    return 'dataProvider' in props ? (
        <Select
            {...props}
            size={size}
            color={color}
            renderTrigger={renderDataProviderTrigger}
            className={cls('select', [className])}
            data-testid={dataTestId}
        />
    ) : (
        <Select
            {...props}
            size={size}
            color={color}
            renderTrigger={renderTrigger}
            className={cls('select', [className])}
            data-testid={dataTestId}
        />
    );
});

export interface FilterExpressionInputProps
    extends Omit<
            TextAreaProps,
            'color' | 'size' | 'isTextarea' | 'sizeControlType' | 'textColor' | 'textRole' | 'restrictNewlines'
        >,
        Pick<FilterExpressionProps, 'onRemove' | 'error'>,
        TestProps,
        DOMProps {
    className?: string;
    hasRemovalIcon?: boolean;
    maskConfig?: MaskConfig;
}

/**
 * Подкомпонент FilterExpression, используется для создания чипса-инпута.
 * Испрользуется для ввода числового, тестового значения или даты для фильтрации
 */
export const FilterExpressionInput = genericMemo(
    forwardRef<HTMLTextAreaElement, FilterExpressionInputProps>(function FilterExpressionInput(props, ref) {
        const {
            className,
            'data-testid': dataTestId = 'FilterExpressionInput',
            onRemove,
            onBlur,
            onFocus,
            value = '',
            hasRemovalIcon,
            maskConfig,
            isDisabled,
            error,
            ...restProps
        } = props;
        const { size } = useContext(FilterChipContext);
        const containerRef = useContext(FilterExpressionRefContext);
        const { value: isFocused, toggleOn: setFocused, toggleOff: setBlurred } = useToggle(restProps.autoFocus);

        const onBlurHandler: FocusEventHandler<HTMLInputElement> = useCallback(
            event => {
                setBlurred();
                onBlur?.(event);

                const relatedTarget = event.relatedTarget;

                // Проверяем, что фокус переходит внутрь FilterExpression
                const shouldIgnoreRemove =
                    relatedTarget &&
                    containerRef?.current &&
                    (containerRef.current === relatedTarget || containerRef.current.contains(relatedTarget));

                if (!value && !shouldIgnoreRemove) {
                    onRemove?.();
                }
            },
            [onRemove, onBlur, value, setBlurred, containerRef],
        );

        const onFocusHandler: FocusEventHandler<HTMLInputElement> = useCallback(
            event => {
                setFocused();
                onFocus?.(event);
            },
            [setFocused, onFocus],
        );

        const textInputProps: TextInputProps = {
            value: isFocused ? value : maxLengthCut(value, 40),
            ...restProps,
            // Нужно, чтобы сделать весь текст серым
            highlightRules: [/.*/],
            'data-testid': dataTestId,
            className: cls('input-control', {
                disabled: isDisabled,
                focused: !value,
            }),
            color: 'transparent',
            size: size,
            isTextarea: true,
            sizeControlType: 'auto',
            onBlur: onBlurHandler,
            restrictNewlines: true,
            onFocus: onFocusHandler,
            isDisabled,
        };

        return (
            <FilterChip
                isInteractive
                onRemove={hasRemovalIcon ? onRemove : undefined}
                className={cls('input', [className])}
                isDisabled={isDisabled}
                error={error}
                data-testid={`${dataTestId}.chip`}
            >
                {maskConfig ? (
                    <MaskedTextInput ref={ref} {...textInputProps} maskConfig={maskConfig} />
                ) : (
                    <TextInput ref={ref} {...textInputProps} />
                )}
            </FilterChip>
        );
    }),
);

export interface FilterExpressionAddProps extends TestProps, Loggable, DOMProps {
    className?: string;
    onClick: () => void;
}

export const FilterExpressionAdd = genericMemo(function FilterExpressionAdd({
    className,
    onClick,
    logNode,
    'data-testid': dataTestId,
}: FilterExpressionAddProps) {
    const { LogProvider, logClick } = useLogger('FilterExpressionAdd', logNode);

    const { size } = useContext(FilterChipContext);
    const { iconSize } = useFilterChipAddonSizes(size);

    const onClickHandler = useCallback(() => {
        onClick();
        logClick({ type: 'addFilterExpression' });
    }, [onClick, logClick]);

    return (
        <LogProvider>
            <FilterChip onClick={onClickHandler} className={cls('add', [className])} data-testid={dataTestId}>
                <IconPlusSkinny size={iconSize} color={'gray'} />
            </FilterChip>
        </LogProvider>
    );
});
